import { useEffect, useRef, useState } from 'react';
import { POST_TYPE_OPTIONS, PRIMARY_TOPIC_KEYS, TOPIC_OPTIONS } from '../constants/hixrs';
import { getPostTypeOption, getTopicLabel, getTopicOptionByKey, getTopicStyle } from '../utils/hixrs';
import { readCompressedImage } from '../services/imageUpload';
import { CommentBubbleIcon } from '../components/common';

export function CreateScreen({ onPost, requireLogin, user, defaultPostType = 'quick' }) {
  const [question, setQuestion] = useState('');
  const [sideA, setSideA] = useState('');
  const [sideB, setSideB] = useState('');
  const [topic, setTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [showMoreTopics, setShowMoreTopics] = useState(false);
  const [postType, setPostType] = useState(getPostTypeOption(defaultPostType).key);
  const [postImage, setPostImage] = useState(null);
  const postImageRef = useRef();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const primaryTopics = PRIMARY_TOPIC_KEYS.map(getTopicOptionByKey).filter(Boolean);
  const moreTopics = TOPIC_OPTIONS.filter(topicOption => !PRIMARY_TOPIC_KEYS.includes(topicOption.key));
  const selectedPostType = getPostTypeOption(postType);
  const isThought = postType === 'thought';
  const cleanCustomTopic = customTopic.trim().replace(/\s+/g, ' ').slice(0, 32);
  const questionChars = question.trim().length;
  const sideAChars = sideA.trim().length;
  const sideBChars = sideB.trim().length;
  const questionLimit = isThought ? 280 : 180;
  const createSteps = [
    { label: isThought ? 'Thought' : 'Question', done: questionChars >= 8 },
    { label: isThought ? 'Open' : 'Sides', done: isThought || (sideAChars > 0 && sideBChars > 0 && sideA.trim().toLowerCase() !== sideB.trim().toLowerCase()) },
    { label: 'Topic', done: Boolean(topic) },
  ];
  const completedSteps = createSteps.filter(step => step.done).length;
  const readyToPost = completedSteps === createSteps.length;

  useEffect(() => {
    setPostType(getPostTypeOption(defaultPostType).key);
  }, [defaultPostType]);

  const handlePostImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file);
      if (compressed.length > 2500000) {
        setError('This image is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setPostImage(compressed);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Could not read that image. Try another file.');
      e.target.value = '';
    }
  };

  const removePostImage = () => {
    setPostImage(null);
    if (postImageRef.current) postImageRef.current.value = '';
  };
  const handleSubmit = () => {
    requireLogin(async () => {
      const cleanQuestion = question.trim();
      const cleanSideA = sideA.trim();
      const cleanSideB = sideB.trim();
      const cleanTopic = topic.trim();

      if (cleanQuestion.length < 8) { setError(isThought ? 'Write a clearer thought first' : 'Make the question a little sharper'); return; }
      if (!isThought && (!cleanSideA || !cleanSideB)) { setError('Add both sides'); return; }
      if (!isThought && cleanSideA.toLowerCase() === cleanSideB.toLowerCase()) { setError('Both sides cannot be the same'); return; }
      if (!cleanTopic) { setError('Pick a topic'); return; }

      setError('');
      setLoading(true);
      try {
        await onPost({ question: cleanQuestion, sideA: isThought ? '' : cleanSideA, sideB: isThought ? '' : cleanSideB, topic: cleanTopic, postType, image: postImage });
        setQuestion(''); setSideA(''); setSideB(''); setTopic(''); setCustomTopic(''); setShowMoreTopics(false); setPostType(getPostTypeOption(defaultPostType).key); setPostImage(null);
        if (postImageRef.current) postImageRef.current.value = '';
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } catch (e) {
        console.error(e);
        setError('Could not post. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="screen-wrap">
      <div className="create-wrap">
        <div className="create-header">
          <div>
            <div className="create-kicker">{isThought ? 'Create Thought' : 'Create Hix'}</div>
            <div className="screen-title">{isThought ? 'Share a thought people can discuss' : 'Frame a clean two-sided question'}</div>
            <div className="create-sub">{isThought ? 'Open Thoughts are for observations, stories, and ideas that should start with comments instead of sides.' : 'Good Hixs are specific, easy to lock in on, and strong enough that both sides can defend themselves.'}</div>
          </div>
          <div className={`create-readiness ${readyToPost ? 'ready' : ''}`}>
            {readyToPost ? 'Ready' : `${completedSteps}/${createSteps.length}`}
          </div>
        </div>

        {success && <div className="success-banner">Post is live.</div>}
        {error && <div className="error-banner">{error}</div>}

        <div className="create-grid">
          <div className="create-card composer-card">
            <div className="create-progress">
              {createSteps.map(step => (
                <div key={step.label} className={step.done ? 'done' : ''}>
                  <span></span>
                  <strong>{step.label}</strong>
                </div>
              ))}
            </div>

            <div className="c-section">
              <div className="field-row">
                <div className="c-label">{isThought ? 'Thought' : 'Question'}</div>
                <div className="field-count">{question.length}/{questionLimit}</div>
              </div>
              <textarea className="c-input" rows={4}
                maxLength={questionLimit}
                placeholder={isThought ? 'I think people pretend to enjoy clubbing because everyone else does.' : 'Will Arsenal win the Champions League this season?'}
                value={question} onChange={e => setQuestion(e.target.value)} />
              <div className="field-hint">{isThought ? 'Write one clear idea. People can respond immediately, no lock-in needed.' : 'Ask one clear question. Avoid vague posts that only one side can answer.'}</div>
            </div>

            <div className={`c-section sides-section ${isThought ? 'thought-mode' : ''}`} aria-hidden={isThought}>
              <div className="field-row">
                <div className="c-label">Sides</div>
                <div className={`field-count ${sideAChars > 0 && sideBChars > 0 && sideA.trim().toLowerCase() === sideB.trim().toLowerCase() ? 'field-warning' : ''}`}>
                  {isThought ? 'Not needed' : sideAChars > 0 && sideBChars > 0 && sideA.trim().toLowerCase() === sideB.trim().toLowerCase() ? 'Make them different' : 'Required'}
                </div>
              </div>
              <div className="side-grid">
                <label className="side-field side-a-field">
                  <span>Side A</span>
                  <input className="side-inp red-inp" placeholder="Yes"
                    maxLength={40}
                    disabled={isThought}
                    value={sideA} onChange={e => setSideA(e.target.value)} />
                </label>
                <label className="side-field side-b-field">
                  <span>Side B</span>
                  <input className="side-inp blue-inp" placeholder="No"
                    maxLength={40}
                    disabled={isThought}
                    value={sideB} onChange={e => setSideB(e.target.value)} />
                </label>
              </div>
            </div>

            <div className="c-section">
              <div className="field-row">
                <div className="c-label">Post type</div>
                <div className="field-count">{selectedPostType.label}</div>
              </div>
              <div className="post-type-grid">
                {POST_TYPE_OPTIONS.map(type => (
                  <button key={type.key} type="button"
                    className={`post-type-option type-${type.key} ${postType === type.key ? 'type-on' : ''}`}
                    onClick={() => setPostType(type.key)}>
                    <span className="post-type-name">{type.label}</span>
                    <span className="post-type-desc">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="c-section">
              <div className="field-row">
                <div className="c-label">Topic</div>
                <div className="field-count">{topic || 'Pick one'}</div>
              </div>
              <div className="topic-chips compact-topic-chips">
                {primaryTopics.map(t => (
                  <button key={t.key} type="button"
                    className={`topic-chip ${topic === t.label ? 'chip-on' : ''}`}
                    onClick={() => setTopic(t.label)}>{t.label}</button>
                ))}
                <button type="button"
                  className={`topic-chip more-topic-toggle ${showMoreTopics ? 'chip-on' : ''}`}
                  onClick={() => setShowMoreTopics(prev => !prev)}>More</button>
              </div>
              {showMoreTopics && (
                <div className="more-topic-panel">
                  <div className="more-topic-grid">
                    {moreTopics.map(t => (
                      <button key={t.key} type="button"
                        className={`topic-chip ${topic === t.label ? 'chip-on' : ''}`}
                        onClick={() => setTopic(t.label)}>{t.label}</button>
                    ))}
                  </div>
                  <div className="custom-topic-row">
                    <input className="custom-topic-input"
                      placeholder="Create topic, e.g. Champions League"
                      maxLength={32}
                      value={customTopic}
                      onChange={e => setCustomTopic(e.target.value)} />
                    <button type="button" className="custom-topic-use"
                      onClick={() => {
                        if (cleanCustomTopic.length < 2) { setError('Custom topic needs at least 2 characters.'); return; }
                        setTopic(cleanCustomTopic);
                        setShowMoreTopics(false);
                        setError('');
                      }}>
                      Use
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="c-section">
              <div className="field-row">
                <div className="c-label">Image</div>
                <div className="field-count">Optional</div>
              </div>
              <div className="post-image-uploader">
                {postImage ? (
                  <div className="post-image-preview-wrap">
                    <img src={postImage} alt="Post preview" className="post-image-preview" />
                    <button type="button" className="image-remove-btn" onClick={removePostImage}>Remove</button>
                  </div>
                ) : (
                  <button type="button" className="image-upload-btn" onClick={() => postImageRef.current.click()}>
                    <strong>Add image</strong>
                    <span>{isThought ? 'Optional mood for the thought' : 'Optional context for the Hix'}</span>
                  </button>
                )}
                <input ref={postImageRef} type="file" accept="image/*" onChange={handlePostImage} style={{ display: 'none' }} />
              </div>
              <div className="field-hint">Upload from your device. Large images are compressed before posting.</div>
            </div>
            <button className="post-submit" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Posting...' : isThought ? 'Post Thought' : 'Post Hix'}
            </button>
          </div>

          <div className="create-card preview-card">
            <div className="preview-head">
              <div>
                <div className="preview-label">Preview</div>
                <span>{readyToPost ? 'Ready to post' : 'Finish the required fields'}</span>
              </div>
              <div className={`preview-status ${readyToPost ? 'ready' : ''}`}>{readyToPost ? 'Live-ready' : 'Draft'}</div>
            </div>
            <div className={`preview-post hix-theme-${selectedPostType.key} ${isThought ? 'open-thought-preview' : ''}`}>
              <div className="post-top">
                <div className="post-meta-row">
                  <span className="post-topic" style={getTopicStyle(topic)}>{topic || 'Topic'}</span>
                  <span className={`post-type-badge type-${selectedPostType.key}`}>{selectedPostType.label}</span>
                </div>
                <span className="post-heat">{isThought ? 'Open' : 'Split 0%'}</span>
              </div>
              <div className="post-question">{question.trim() || (isThought ? 'Your Open Thought will appear here.' : 'Your Hix question will appear here.')}</div>
              {postImage && <img className="post-image" src={postImage} alt="Preview attachment" />}
              {!isThought && <div className="vote-row preview-votes">
                <button className="vote-btn red-btn"><span>{sideA.trim() || 'Side A'}</span></button>
                <button className="vote-btn blue-btn"><span>{sideB.trim() || 'Side B'}</span></button>
              </div>}
                            <div className="comment-strip preview-comment-strip">
                <button type="button" className="heat-action preview-action" disabled>
                  <span className="heat-fire">&#128293;</span><span className="heat-label">Heat</span><strong>0</strong>
                </button>
                <button type="button" className="comment-open-btn" disabled aria-label="0 comments" title="0 comments">
                  <CommentBubbleIcon />
                  <strong>0</strong>
                  <span className="sr-only">comments</span>
                </button>
              </div>
            </div>
            <div className="preview-checklist">
              {createSteps.map(step => (
                <span key={step.label} className={step.done ? 'done' : ''}>{step.done ? 'Done' : 'Needed'} · {step.label}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export function EditHixModal({ post, onClose, onSave }) {
  const [question, setQuestion] = useState(post?.question || '');
  const [sideA, setSideA] = useState(post?.sideA || '');
  const [sideB, setSideB] = useState(post?.sideB || '');
  const [topic, setTopic] = useState(getTopicLabel(post?.topic || ''));
  const [customTopic, setCustomTopic] = useState('');
  const [showMoreTopics, setShowMoreTopics] = useState(false);
  const [postType, setPostType] = useState(getPostTypeOption(post?.postType).key);
  const [postImage, setPostImage] = useState(post?.image || null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const imageRef = useRef();
  const primaryTopics = PRIMARY_TOPIC_KEYS.map(getTopicOptionByKey).filter(Boolean);
  const moreTopics = TOPIC_OPTIONS.filter(topicOption => !PRIMARY_TOPIC_KEYS.includes(topicOption.key));
  const cleanCustomTopic = customTopic.trim().replace(/\s+/g, ' ').slice(0, 32);
  const hasLockIns = Number(post?.totalVotes || 0) > 0;
  const isThought = postType === 'thought';
  const questionLimit = isThought ? 280 : 180;

  if (!post) return null;

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file);
      if (compressed.length > 2500000) {
        setError('This image is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setPostImage(compressed);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Could not read that image. Try another file.');
      e.target.value = '';
    }
  };

  const removeImage = () => {
    setPostImage(null);
    if (imageRef.current) imageRef.current.value = '';
  };

  const submit = async () => {
    const cleanQuestion = question.trim();
    const cleanSideA = sideA.trim();
    const cleanSideB = sideB.trim();
    const cleanTopic = topic.trim();

    if (cleanQuestion.length < 8) { setError(isThought ? 'Thought needs at least 8 characters.' : 'Question needs at least 8 characters.'); return; }
    if (!cleanTopic) { setError('Pick a topic.'); return; }
    if (!hasLockIns && !isThought) {
      if (!cleanSideA || !cleanSideB) { setError('Add both sides.'); return; }
      if (cleanSideA.toLowerCase() === cleanSideB.toLowerCase()) { setError('Both sides cannot be the same.'); return; }
    }

    setSaving(true);
    setError('');
    try {
      await onSave(post, {
        question: cleanQuestion,
        sideA: isThought ? '' : cleanSideA,
        sideB: isThought ? '' : cleanSideB,
        topic: cleanTopic,
        postType,
        image: postImage
      });
      onClose();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Could not save this Hix.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-hix-overlay" onClick={e => e.target.className === 'edit-hix-overlay' && !saving && onClose()}>
      <div className="edit-hix-modal">
        <div className="edit-hix-head">
          <div>
            <div className="edit-hix-kicker">{isThought ? 'Edit Thought' : 'Edit Hix'}</div>
            <div className="edit-hix-title">Update your post</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving}>Close</button>
        </div>
        {hasLockIns && !isThought && <div className="edit-hix-note">Sides are locked after the first lock-in so people&apos;s choices do not get rewritten.</div>}
        {error && <div className="error-banner">{error}</div>}

        <div className="edit-hix-body">
          <label className="edit-hix-field">
            <span>{isThought ? 'Thought' : 'Question'}</span>
            <textarea rows={4} maxLength={questionLimit} value={question} onChange={e => setQuestion(e.target.value)} />
            <small>{question.length}/{questionLimit}</small>
          </label>

          {!isThought && <div className="edit-hix-sides">
            <label className="edit-hix-field">
              <span>Side A</span>
              <input value={sideA} maxLength={40} disabled={hasLockIns} onChange={e => setSideA(e.target.value)} />
            </label>
            <label className="edit-hix-field">
              <span>Side B</span>
              <input value={sideB} maxLength={40} disabled={hasLockIns} onChange={e => setSideB(e.target.value)} />
            </label>
          </div>}

          <div className="edit-hix-section">
            <div className="edit-hix-label">Post type</div>
            <div className="edit-hix-segment">
              {POST_TYPE_OPTIONS.map(type => (
                <button key={type.key} type="button" className={postType === type.key ? 'active' : ''} onClick={() => setPostType(type.key)}>{type.label}</button>
              ))}
            </div>
          </div>

          <div className="edit-hix-section">
            <div className="edit-hix-label">Topic</div>
            <div className="topic-chips compact-topic-chips">
              {primaryTopics.map(t => (
                <button key={t.key} type="button" className={`topic-chip ${topic === t.label ? 'chip-on' : ''}`} onClick={() => setTopic(t.label)}>{t.label}</button>
              ))}
              <button type="button" className={`topic-chip more-topic-toggle ${showMoreTopics ? 'chip-on' : ''}`} onClick={() => setShowMoreTopics(prev => !prev)}>More</button>
            </div>
            {showMoreTopics && (
              <div className="more-topic-panel edit-more-topic-panel">
                <div className="more-topic-grid">
                  {moreTopics.map(t => (
                    <button key={t.key} type="button" className={`topic-chip ${topic === t.label ? 'chip-on' : ''}`} onClick={() => setTopic(t.label)}>{t.label}</button>
                  ))}
                </div>
                <div className="custom-topic-row">
                  <input className="custom-topic-input" placeholder="Create topic" maxLength={32} value={customTopic} onChange={e => setCustomTopic(e.target.value)} />
                  <button type="button" className="custom-topic-use" onClick={() => {
                    if (cleanCustomTopic.length < 2) { setError('Custom topic needs at least 2 characters.'); return; }
                    setTopic(cleanCustomTopic);
                    setShowMoreTopics(false);
                    setError('');
                  }}>Use</button>
                </div>
              </div>
            )}
          </div>

          <div className="edit-hix-section">
            <div className="edit-hix-label">Image</div>
            {postImage && <img className="edit-hix-image-preview" src={postImage} alt="Hix preview" />}
            <div className="edit-hix-image-actions">
              <button type="button" onClick={() => imageRef.current.click()}>Change image</button>
              {postImage && <button type="button" className="danger" onClick={removeImage}>Remove image</button>}
              <input ref={imageRef} type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
            </div>
          </div>
        </div>

        <div className="edit-hix-actions">
          <button className="confirm-cancel" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="profile-save-btn" type="button" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}
// PROFILE SCREEN
